/**
 * Revenue Mix — multi-contract composition view.
 *
 * Answers: "Across this portfolio, what's our contracted revenue stream, how
 * does it step down as PPAs expire, and where's our merchant exposure?"
 *
 * What we show:
 *   1. KPI strip — contracted $/yr (next 12m, next 5y), total contract value
 *   2. Revenue cliff chart — stacked area, one band per PPA, monthly granularity
 *   3. Contracted-vs-merchant split — donut or split bar
 *   4. Expiry timeline — Gantt-style strip per contract
 *
 * Revenue model (Phase 1 — coarse):
 *   For each PPA, monthly contracted revenue =
 *     buyers_share × (MEG_MWh / 12) × Fixed Price for that month
 *   Merchant share = (1 − buyers_share) × (MEG / 12) × <merchant price proxy>
 *
 * MEG is a conservative proxy for actual generation. When we wire real
 * generation forecasts (P50/P90) and spot price forecasts, this engine drops
 * out the same shape with better numbers.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip, Skeleton, Alert, Card, CardContent, IconButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';

import { useThemeColors } from '../theme/ThemeModeContext';
import PpaWorkspaceTabs from './PpaWorkspaceTabs';
import ErrorBoundary from './ErrorBoundary';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtAud  = (n) => (n == null ? '—' : `A$${Math.round(n).toLocaleString('en-AU')}`);
const fmtMm   = (n) => (n == null ? '—' : `A$${(n / 1_000_000).toFixed(1)}M`);
const fmtDate = (d) => (d == null ? '—' : new Date(d).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }));

// ── Colours for each PPA band on the stacked chart ────────────────────────────
const BAND_PALETTE = [
  '#D98A2B', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444',
  '#14b8a6', '#6366f1', '#d4930d', '#ec4899', '#22c55e',
];

// Revenue projection engine is server-side now —
//   GET /api/portfolios/<id>/revenue/cliff?years=10
// returns months[], per_contract[].monthly_revenue_aud[],
// total_monthly_revenue_aud[], summary{next_12m_aud, next_5y_aud, ...}.
// All previous client-side `priceForDate` / `monthlyContractedRevenue` /
// `makeMonthlyHorizon` logic was deleted — see
// modules/contracts/revenue_calc.py:monthly_revenue_cliff (single source).

// ──────────────────────────────────────────────────────────────────────────────

const KpiTile = ({ label, value, hint }) => (
  <Box sx={{
    flex: '1 1 0', minWidth: 130,
    p: 1.5, borderRadius: 1,
    bgcolor: 'rgba(217, 138, 43, 0.04)',
    border: '1px solid rgba(217, 138, 43, 0.10)',
  }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary',
                      letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'text.primary',
                      lineHeight: 1.2, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

// Each section is wrapped in an ErrorBoundary so one broken chart doesn't
// take down the whole Revenue Mix page.
const Section = ({ title, right, children }) => (
  <Card variant="outlined" sx={{ mb: 2, borderColor: 'divider', borderRadius: 1.5 }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary',
                          letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        {right}
      </Stack>
      <ErrorBoundary name={title} compact>
        {children}
      </ErrorBoundary>
    </CardContent>
  </Card>
);

// ──────────────────────────────────────────────────────────────────────────────

export default function RevenueMix() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  const [contracts, setContracts] = useState([]);
  const [cliff, setCliff]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // We fetch two endpoints in parallel:
  //   /api/contracts?portfolio_id  — needed for the Expiry Timeline section
  //                                  (per-PPA termination dates + labels).
  //   /api/portfolios/<id>/revenue/cliff?years=10 — server-computed monthly
  //                                  contracted-revenue projection (the
  //                                  cliff chart + KPI strip).
  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const params = portfolioId ? { portfolio_id: portfolioId } : {};
      const [cRes, fRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/contracts`, { params }),
        portfolioId
          ? axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/revenue/cliff`, { params: { years: 10 } })
          : Promise.resolve({ data: null }),
      ]);
      const ppas = (cRes.data.contracts || []).filter(c => c.contract_kind === 'PPA');
      setContracts(ppas);
      setCliff(fRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [portfolioId]);

  // ── Map server-side cliff projection → chart-friendly shape ────────────────
  // Server emits ISO date strings; the chart wants Date objects on the x-axis.
  const { months, perContractSeries, totals, kpis } = useMemo(() => {
    const isoMonths = cliff?.months || [];
    const months = isoMonths.map((s) => new Date(s));
    const totals = cliff?.total_monthly_revenue_aud || months.map(() => 0);

    // Map server per_contract[] → chart series with colour + label.
    // Server returns contract_id; we look up the corresponding contract from
    // the /contracts response to surface the counterparty / filename / etc.
    const contractById = Object.fromEntries(contracts.map((c) => [c.contract_id, c]));
    const perContract = (cliff?.per_contract || []).map((row, idx) => {
      const c = contractById[row.contract_id] || {};
      return {
        contract: c,
        label: row.counterparty || c.source?.filename || row.contract_id.slice(0, 8),
        colour: BAND_PALETTE[idx % BAND_PALETTE.length],
        values: row.monthly_revenue_aud,
      };
    });

    // Earliest expiry → "revenue cliff at" date (still client-side; it's a
    // trivial reduction over already-loaded contract metadata).
    const expiryDates = contracts
      .map((c) => c.termination_date)
      .filter(Boolean)
      .sort();

    return {
      months,
      perContractSeries: perContract,
      totals,
      kpis: {
        next12m:    cliff?.summary?.next_12m_aud      ?? 0,
        next5y:     cliff?.summary?.next_5y_aud       ?? 0,
        totalTerm:  cliff?.summary?.horizon_total_aud ?? 0,
        firstExpiry: expiryDates[0] || null,
      },
    };
  }, [contracts, cliff]);

  // ── Empty / loading states ─────────────────────────────────────────────────
  if (loading && !contracts.length) {
    return <Box><PpaWorkspaceTabs /><Box>{[1,2,3].map(i =>
      <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 1.5, borderRadius: 1 }} />)}</Box></Box>;
  }
  if (error) {
    return <Box><PpaWorkspaceTabs /><Alert severity="error">{error}</Alert></Box>;
  }
  if (!contracts.length) {
    return (
      <Box>
        <PpaWorkspaceTabs />
        <Card variant="outlined" sx={{ p: 3, textAlign: 'center', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>No PPAs yet</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
            Upload a PPA in the <b>Contracts</b> tab. The revenue mix builds itself from the extracted price schedules and MEG.
          </Typography>
        </Card>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const chartHasData = totals.some(v => v > 0);

  return (
    <Box>
      <PpaWorkspaceTabs />

      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'text.primary' }}>
          Revenue mix
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      {/* KPI strip ─────────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <KpiTile label="Contracted · next 12m"  value={fmtMm(kpis.next12m)} />
        <KpiTile label="Contracted · next 5y"   value={fmtMm(kpis.next5y)} />
        <KpiTile label="Contracted · total term"value={fmtMm(kpis.totalTerm)} />
        <KpiTile label="First expiry"           value={fmtDate(kpis.firstExpiry)} hint="Earliest PPA termination" />
        <KpiTile label="Active PPAs"            value={contracts.length} />
      </Stack>

      {/* Revenue cliff stacked chart ──────────────────────────────────────── */}
      <Section title="Revenue cliff — contracted $ / month, stacked by PPA"
               right={!chartHasData && (
                 <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }}>
                   Needs `min_expected_generation` + `price_schedule` on each PPA
                 </Typography>
               )}>
        {chartHasData ? (
          <Box sx={{ height: 320 }}>
            <LineChart
              xAxis={[{ data: months, scaleType: 'time',
                        valueFormatter: d => new Date(d).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }) }]}
              yAxis={[{ valueFormatter: v => fmtMm(v) }]}
              series={perContractSeries.map(p => ({
                data: p.values,
                label: p.label,
                color: p.colour,
                area: true,
                stack: 'rev',
                showMark: false,
                valueFormatter: v => fmtAud(v) + ' / mo',
              }))}
              grid={{ horizontal: true }}
              margin={{ left: 70, right: 20, top: 10, bottom: 30 }}
              height={310}
              slotProps={{
                legend: { hidden: contracts.length === 1, position: { vertical: 'top', horizontal: 'right' } },
              }}
            />
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
            Once the extractor populates Buyer's Share, MEG and a price schedule for at least one PPA, this chart will render.
          </Typography>
        )}
      </Section>

      {/* Expiry timeline (Gantt-style) ────────────────────────────────────── */}
      <Section title="Expiry timeline">
        <Stack spacing={1}>
          {contracts.map((c, idx) => (
            <ExpiryBar key={c.contract_id} contract={c} idx={idx} horizonMonths={months} />
          ))}
        </Stack>
      </Section>

      {/* Per-PPA annual revenue table ─────────────────────────────────────── */}
      <Section title="Annual contracted revenue by PPA (next 8 years)">
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                <Box component="th" sx={{ textAlign: 'left', py: 0.75, color: 'text.secondary',
                                          fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Counterparty
                </Box>
                {[0, 1, 2, 3, 4, 5, 6, 7].map(y => (
                  <Box key={y} component="th" sx={{ textAlign: 'right', py: 0.75, color: 'text.secondary',
                                                    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Y{y + 1}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {perContractSeries.map((p) => {
                const yearTotals = Array.from({ length: 8 }, (_, y) =>
                  p.values.slice(y * 12, (y + 1) * 12).reduce((s, v) => s + v, 0)
                );
                return (
                  <Box component="tr" key={p.label} sx={{ borderBottom: '1px dashed', borderBottomColor: 'divider' }}>
                    <Box component="td" sx={{ py: 0.6 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.colour }} />
                        <span>{p.label}</span>
                      </Stack>
                    </Box>
                    {yearTotals.map((t, y) => (
                      <Box key={y} component="td" sx={{ textAlign: 'right', py: 0.6, fontVariantNumeric: 'tabular-nums' }}>
                        {t > 0 ? fmtMm(t) : '—'}
                      </Box>
                    ))}
                  </Box>
                );
              })}
              {/* Total row */}
              <Box component="tr" sx={{ borderTop: '2px solid', borderTopColor: 'divider' }}>
                <Box component="td" sx={{ py: 0.6, fontWeight: 700 }}>Total</Box>
                {[0,1,2,3,4,5,6,7].map(y => {
                  const total = perContractSeries.reduce(
                    (s, p) => s + p.values.slice(y * 12, (y + 1) * 12).reduce((a, v) => a + v, 0),
                    0
                  );
                  return (
                    <Box key={y} component="td" sx={{ textAlign: 'right', py: 0.6, fontWeight: 700,
                                                       fontVariantNumeric: 'tabular-nums' }}>
                      {total > 0 ? fmtMm(total) : '—'}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Section>
    </Box>
  );
}

// ── Expiry bar: shows each contract's term as a horizontal sweep ──────────────
function ExpiryBar({ contract, idx, horizonMonths }) {
  const colour = BAND_PALETTE[idx % BAND_PALETTE.length];
  const horizonStart = horizonMonths[0];
  const horizonEnd   = horizonMonths[horizonMonths.length - 1];
  const start = contract.effective_date ? new Date(contract.effective_date) : null;
  const end   = contract.termination_date ? new Date(contract.termination_date) : null;
  if (!start && !end) return null;

  const clamp = (d) => Math.max(horizonStart.valueOf(), Math.min(horizonEnd.valueOf(), d.valueOf()));
  const sPct = start ? ((clamp(start) - horizonStart.valueOf()) / (horizonEnd.valueOf() - horizonStart.valueOf())) * 100 : 0;
  const ePct = end   ? ((clamp(end)   - horizonStart.valueOf()) / (horizonEnd.valueOf() - horizonStart.valueOf())) * 100 : 100;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 110px', gap: 1, alignItems: 'center' }}>
      <Typography sx={{ fontSize: '0.82rem', color: 'text.primary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {contract.counterparty || contract.source?.filename || contract.contract_id.slice(0, 8)}
      </Typography>
      <Box sx={{ position: 'relative', height: 18, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 4 }}>
        <Box sx={{
          position: 'absolute',
          left: `${sPct}%`,
          width: `${Math.max(0, ePct - sPct)}%`,
          top: 0, bottom: 0,
          bgcolor: colour, opacity: 0.85, borderRadius: 4,
        }} />
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums' }}>
        {fmtDate(start)} → {fmtDate(end)}
      </Typography>
    </Box>
  );
}
